/**
 * 单条提醒（**展示单元** · H 域提醒中心）
 *
 * 依据：原型 `09-提醒中心_原型.html` 的 `.rm-item`（图标 + 标题 + 标签 + 时间 + 描述 + 动作 + 金额）·
 * H 域 PRD **§5.1.4**（每条**二选一**：给动作 or 明确「无需处理」）· **`AC-H1.11`**。
 *
 * ## 🔴 端侧零判定
 *
 * `group` / `state` / `tag` / `actionable` / `mergedCount` / `mergedIds` **全部来自服务端**；
 * 本组件只做「按字段选词 + 排版」——文案来自 `features/reminder-labels.ts`。
 *
 * ## ⚠️ 着色纪律（铁律 8）
 *
 * 「关键」标签用 **`warning`（琥珀）**，**不用 `error`（朱红=已超）** ——
 * 本项目的红色语义是「正 / 已超」，用它标"重要"会让同屏两个红含义冲突。
 * 金额一律 `colored={false}`：提醒里的金额是**待还/预算额**这类绝对额，
 * 按正负着色会把它读成"收入/支出"。
 */

import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { ReminderGroup, ReminderState, type ReminderView } from '@jiaying/shared';
import { Amount } from './Amount.js';
import {
  DONE_LABEL,
  NO_ACTION_TEXT,
  READ_LABEL,
  TAG_LABELS,
  actionHintOf,
  reminderDesc,
  reminderTitle,
  stateBadgeOf,
} from '../features/reminder-labels.js';

export interface ReminderItemProps {
  item: ReminderView;
  currency: string;
  onMarkRead: (item: ReminderView) => void;
  onDone: (item: ReminderView) => void;
  /** 正在提交（禁用按钮 —— **不画点不动的控件**） */
  pending?: boolean;
}

export function ReminderItem({ item, currency, onMarkRead, onDone, pending = false }: ReminderItemProps) {
  const unread = item.state === ReminderState.Unread;
  const earlier = item.group === ReminderGroup.Earlier;
  const desc = reminderDesc(item, currency);
  const action = actionHintOf(item);
  const badge = stateBadgeOf(item);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{ py: 1.25, opacity: earlier ? 0.72 : 1 }}
      data-testid={`reminder-${item.id}`}
      data-group={item.group}
      data-state={item.state}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: unread ? 600 : 400 }}
            data-testid={`reminder-title-${item.id}`}
          >
            {reminderTitle(item)}
          </Typography>
          <Chip
            size="small"
            label={TAG_LABELS[item.tag]}
            color={item.tag === 'key' ? 'warning' : 'default'}
            variant={item.tag === 'key' ? 'filled' : 'outlined'}
          />
          {badge === null ? null : <Chip size="small" label={badge} variant="outlined" />}
          {item.mergedCount > 1 ? (
            <Typography variant="caption" color="text.secondary">
              共 {item.mergedCount} 笔
            </Typography>
          ) : null}
        </Stack>

        {desc === null ? null : (
          <Typography variant="body2" color="text.secondary" data-testid={`reminder-desc-${item.id}`}>
            {desc}
          </Typography>
        )}

        {/* 🔴 `AC-H1.11`：有动作 ⇒ 给动作按钮 + 一句"要做什么"；**没有动作就明确说「无需处理」**
            （不许什么都不显示）。⚠️ 本批动作的**唯一可达终点 = 标记已处理**：
            去还款 / 看账单明细这类**跳转**需要对应页面（本批未接）⇒ 不画点了没反应的按钮。 */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap' }}>
          {item.actionable ? (
            <>
              <Typography variant="caption" color="text.secondary" data-testid={`reminder-hint-${item.id}`}>
                {action}
              </Typography>
              {item.state === ReminderState.Unread || item.state === ReminderState.Read ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={pending}
                  onClick={() => {
                    onDone(item);
                  }}
                  data-testid={`reminder-done-${item.id}`}
                >
                  {DONE_LABEL}
                </Button>
              ) : null}
            </>
          ) : (
            <Typography variant="caption" color="text.secondary" data-testid={`reminder-noaction-${item.id}`}>
              {NO_ACTION_TEXT}
            </Typography>
          )}
          {unread ? (
            <Button
              size="small"
              disabled={pending}
              onClick={() => {
                onMarkRead(item);
              }}
              data-testid={`reminder-read-${item.id}`}
            >
              {READ_LABEL}
            </Button>
          ) : null}
        </Stack>
      </Box>

      {item.amount === null ? null : (
        <Amount
          cents={item.amount}
          currency={currency}
          variant="body2"
          colored={false}
          /**
           * 🔴 **不参与收缩、不折行** —— 与 `Amount.tsx` 默认「可收缩」的差异是有意的。
           *
           * 本行是 `Stack direction="row"`，左侧 `Box` 已经 `flex: 1, minWidth: 0`（会被压缩），
           * 金额若也默认可收缩，窄屏（`≤720px`，或卡片在双列网格里）时 `¥1,234.56` 会被压成
           * 两行 —— 金额断行在财务界面里读起来像**两个数**（比"挤一点"严重得多）。
           * 右对齐的金额本来就该是「不可压的最后一块」。
           */
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          data-testid={`reminder-amount-${item.id}`}
        />
      )}
    </Stack>
  );
}
